import { assetStatusTypes, assetTypes, isValidAssetStatusType } from '@repo/types/asset';
import type { DraftAsset } from '@repo/types/draft';
import { type OrderAsset, isISOMOrder, isSalesOrder } from '@repo/types/order';
import type { QuoteAsset } from '@repo/types/quotation';
import { type ModifyReasonCodesData, isModifyReasonCodes } from '@repo/types/store-details';
import type { Preference } from '@repo/utils';
import { invariant, isDefined, isEmpty } from '@repo/utils/type.utils';

export const isPaid = (data: OrderAsset) => {
    // If any payment status in orderData.assetDetails._embedded.paymentPlans is FULLY_PAID, return true
    if (!data.assetDetails._embedded?.paymentPlans) {
        return false;
    }
    return data.assetDetails._embedded.paymentPlans.some((paymentPlan) => paymentPlan.status === 'FULLY_PAID');
};

export const isTransportOrder = (order: OrderAsset) =>
    !isEmpty(order.assetDetails._embedded?.Articles?.filter((el) => el.importDetails));

export const preventCancelation = (data: OrderAsset) => {
    const order = data.assetDetails;
    const isCancelled = order.orderStatus === assetStatusTypes.CANCELLED || isPaid(data);
    const isFullfilment = order._embedded?.paymentPlans?.some((paymentPlan) => paymentPlan.timing === 'ON_FULFILMENT');
    const isCashPayment = order._embedded?.paymentPlans?.some((paymentPlan) => paymentPlan.type === 'CASH');

    const isButtonDisabled = isCancelled || Boolean(isFullfilment && isCashPayment);

    let reasonText: string;

    if (isFullfilment && isCashPayment) {
        reasonText = 'order_action_disabled_fulfilment';
    } else {
        reasonText = 'order_action_disabled';
    }

    return { isButtonDisabled, reasonText };
};

const handleOrderModification = (data: OrderAsset) => {
    const order = data.assetDetails;
    const isCancelled = order.orderStatus === assetStatusTypes.CANCELLED || isPaid(data);
    const isReferenceItemOrder = Boolean(order._embedded?.Articles?.some((article) => article.isReferenceItem));

    const isUnpaidTransportDeskOrder = isTransportOrder(data) && !isPaid(data);
    const isSelfServeIsomOrder = isCashAndCarryOrder(data) && isISOMOrder(data);

    const isModificationButtonDisabled =
        isCancelled || isReferenceItemOrder || isUnpaidTransportDeskOrder || isSelfServeIsomOrder;
    let modificationReasonText;

    if (isReferenceItemOrder) {
        modificationReasonText = 'order_action_disabled_reference_item';
    } else if (isUnpaidTransportDeskOrder) {
        modificationReasonText = 'order_action_disabled_transport_desk_order';
    } else if (isSelfServeIsomOrder) {
        modificationReasonText = 'order_action_disabled_self_serve_order';
    } else {
        modificationReasonText = 'order_action_disabled';
    }

    return {
        isModificationButtonDisabled,
        modificationReasonText,
    };
};

const handleDraftModification = (data: DraftAsset, assetId: string | null) => {
    invariant(assetId, 'Draft id is missing');
    const draft = data.assetDetails;
    const isModificationButtonDisabled = draft.status === assetStatusTypes.CONVERTED;
    return {
        isModificationButtonDisabled,
        modificationReasonText: isModificationButtonDisabled ? 'draft_action_disabled_modify' : '',
    };
};

const handleQuoteModification = (data: QuoteAsset) => {
    const { status } = data.assetDetails;
    invariant(isValidAssetStatusType(status), 'Asset status is not valid');
    const isModificationButtonDisabled = Object.values(assetStatusTypes).includes(status);
    let modificationReasonText = '';

    if (isModificationButtonDisabled) {
        switch (status) {
            case assetStatusTypes.CONVERTED: {
                modificationReasonText = 'quotation_action_disabled_modify_convert';
                break;
            }
            case assetStatusTypes.CANCELED: {
                modificationReasonText = 'quotation_action_disabled_modify_cancel';
                break;
            }
            case assetStatusTypes.EXPIRED: {
                modificationReasonText = 'quotation_action_disabled_modify_expired';
                break;
            }
        }
    }

    return { isModificationButtonDisabled, modificationReasonText };
};

export const checkAssetModificationOptions = (data: OrderAsset | DraftAsset | QuoteAsset, assetId: string | null) => {
    const dataAssetType = data.assetType;
    switch (dataAssetType) {
        case assetTypes.order: {
            return handleOrderModification(data);
        }
        case assetTypes.draft: {
            return handleDraftModification(data, assetId);
        }
        case assetTypes.quote: {
            return handleQuoteModification(data);
        }
        default: {
            dataAssetType satisfies never;
            return;
        }
    }
};

export const canPay = (order: OrderAsset) => {
    const toPay = order.assetDetails.Amounts?.ToPay?.grossAmount && order.assetDetails.Amounts.ToPay.grossAmount > 0;

    const isPaidOrder = isPaid(order);

    return Boolean(
        order.assetDetails.orderStatus !== assetStatusTypes.CANCELLED &&
        order.assetDetails.orderStatus !== assetStatusTypes.COMPLETED &&
        !isPaidOrder &&
        toPay
    );
};

export const canPayOnline = (order: OrderAsset, preferences?: Preference[] | null) => {
    if (
        order.assetDetails._embedded?.deliveryArrangements ||
        order.assetDetails._embedded?.providedServices ||
        order.assetDetails.Reservations
    ) {
        if (preferences) {
            if (isTransportOrder(order)) {
                const showLinkForTdOrder = preferences.find((x) => {
                    if (!x.name) {
                        return false;
                    }
                    return x.name.includes('SHOW_PAYORDER_LINK_FOR_TRANSPORT_ORDER');
                });

                return showLinkForTdOrder?.value === 'true';
            }

            const payEnabled = preferences.find((x) => {
                if (!x.name) {
                    return false;
                }
                return x.name.includes('ENABLE_PAYORDER_ONLINE');
            });
            if (payEnabled?.value === 'true') {
                return true;
            }
        }
    }
    return false;
};

export const isCashAndCarryOrder = (order: OrderAsset) => Boolean(order.assetDetails.cashAndCarryOrder);

export const hasReservation = (order: OrderAsset) => !isEmpty(order.assetDetails.Reservations);

export const hasNoStockDelivery = (order: OrderAsset) =>
    order.assetDetails._embedded?.deliveryArrangements?.some((service) =>
        // NO_STOCK_DELIVERY_ID SGR30000937
        service._embedded?.deliveryServices?.some((deliveryService) => deliveryService.serviceId === 'SGR30000937')
    ) ?? false;

export const isEligibleForTermsOfConditions = (order: OrderAsset) => {
    if (
        !isEmpty(order.assetDetails._embedded?.deliveryArrangements) ||
        !isEmpty(order.assetDetails._embedded.providedServices) ||
        isDefined(order.assetDetails._embedded.selectedPickservice)
    ) {
        if (isSalesOrder(order) || isISOMOrder(order)) {
            return true;
        }
    }
    return false;
};

export const canPrintTemsAndCondtion = (preferences?: Preference[] | null) => {
    if (!preferences) {
        return false;
    }
    const pref = preferences.find((x) => {
        if (!x.name) {
            return false;
        }
        return x.name.includes('DEFAULT_PRINT_TERMS_AND_CONDITION_OPTION');
    });
    if (pref) {
        return pref.value === 'true';
    }
    return false;
};

export const DefaultModifyOrderReasons: ModifyReasonCodesData[] = ['CUSCHMIND'];

const normalizePreferenceValue = (value: Preference['value']): string[] => {
    if (!value) {
        return [];
    }
    if (typeof value === 'string') {
        return [value];
    }
    return value.map((item) => (typeof item === 'string' ? item : item.key));
};

export const getOrderModificationReasons = (
    reasonCodesName: string,
    preferences?: Preference[] | null
): ModifyReasonCodesData[] => {
    if (!preferences) {
        return DefaultModifyOrderReasons;
    }

    const preference = preferences.find((preference) => preference.name?.includes(reasonCodesName));

    const preferenceValues = normalizePreferenceValue(preference?.value);
    const result = preferenceValues.filter(isModifyReasonCodes);

    return result.length > 0 ? result : DefaultModifyOrderReasons;
};
